import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import './Analytics.css';

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dateRange, setDateRange] = useState('30'); // Last 30 days by default
  const [loading, setLoading] = useState(true);

  // Color palette for charts
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/analytics/data');
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      } else {
        console.error('Failed to fetch analytics data');
        setAnalyticsData(null);
      }
    } catch (error) {
      console.error('Error loading analytics data:', error);
      setAnalyticsData(null);
    }
    setLoading(false);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    // Data will reload automatically due to useEffect dependency
  };

  const handleClearData = async () => {
    if (window.confirm('Are you sure you want to clear all analytics data? This action cannot be undone.')) {
      try {
        // Note: Backend doesn't have a clear endpoint yet, this would need to be implemented
        // For now, we'll just reload the data
        await loadAnalyticsData();
      } catch (error) {
        console.error('Error clearing analytics data:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="loading-spinner"></div>
        <p>Loading analytics data...</p>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="analytics-error">
        <p>Error loading analytics data</p>
        <button onClick={loadAnalyticsData} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  // Prepare data for charts
  const getDailyUsageForLastDays = (days) => {
    const today = new Date();
    const dailyUsage = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split('T')[0];

      dailyUsage.push({
        date: dateKey,
        clicks: analyticsData.dailyStats[dateKey] || 0,
        formattedDate: date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })
      });
    }

    return dailyUsage;
  };

  const getTopApps = (limit) => {
    return Object.entries(analyticsData.appClicks)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([name, clicks]) => ({ name, clicks }));
  };

  const dailyUsageData = getDailyUsageForLastDays(parseInt(dateRange));
  const topAppsData = getTopApps(10);
  
  // Prepare category data for pie chart
  const categoryData = Object.entries(analyticsData.categoryClicks).map(([category, clicks]) => ({
    name: category,
    value: clicks,
    percentage: ((clicks / analyticsData.totalClicks) * 100).toFixed(1)
  }));

  // Calculate summary stats
  const totalAppsUsed = Object.keys(analyticsData.appClicks).length;
  const averageDailyClicks = analyticsData.totalClicks / Math.max(Object.keys(analyticsData.dailyStats).length, 1);
  const mostPopularApp = topAppsData.length > 0 ? topAppsData[0] : null;

  return (
    <div className="analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <div className="header-left">
          <h1>📊 Analytics Dashboard</h1>
        </div>
        <div className="header-right">
          <select 
            value={dateRange} 
            onChange={(e) => handleDateRangeChange(e.target.value)}
            className="date-range-selector"
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
          <button 
            onClick={handleClearData}
            className="clear-data-button"
            title="Clear all analytics data"
          >
            🗑️ Clear Data
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="summary-stats">
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <h3>Total Clicks</h3>
            <p className="stat-number">{analyticsData.totalClicks.toLocaleString()}</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🚀</div>
          <div className="stat-content">
            <h3>Apps Used</h3>
            <p className="stat-number">{totalAppsUsed}</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <h3>Daily Average</h3>
            <p className="stat-number">{averageDailyClicks.toFixed(1)}</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <h3>Most Popular</h3>
            <p className="stat-text">{mostPopularApp ? mostPopularApp.name : 'None'}</p>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        
        {/* Daily Usage Trend */}
        <div className="chart-container">
          <div className="chart-header">
            <h2>📅 Daily Usage Trend</h2>
            <p>App launches over time</p>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyUsageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="formattedDate" 
                  stroke="#6b7280"
                  fontSize={12}
                />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="clicks" 
                  stroke="#3b82f6" 
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Apps */}
        <div className="chart-container">
          <div className="chart-header">
            <h2>🏆 Most Popular Apps</h2>
            <p>Apps ranked by usage</p>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topAppsData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" stroke="#6b7280" fontSize={12} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke="#6b7280" 
                  fontSize={12}
                  width={120}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar 
                  dataKey="clicks" 
                  fill="#10b981"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category Distribution */}
        <div className="chart-container">
          <div className="chart-header">
            <h2>🎨 Usage by Category</h2>
            <p>Distribution across app categories</p>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({name, percentage}) => `${name} (${percentage}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="chart-container recent-activity">
          <div className="chart-header">
            <h2>🕐 Recent Activity</h2>
            <p>Latest app launches</p>
          </div>
          <div className="recent-activity-list">
            {analyticsData.clickHistory.slice(-10).reverse().map((click, index) => (
              <div key={index} className="activity-item">
                <div className="activity-info">
                  <span className="app-name">{click.appName}</span>
                  <span className="category-tag" style={{backgroundColor: '#f3f4f6'}}>
                    {click.category}
                  </span>
                </div>
                <div className="activity-time">
                  {new Date(click.timestamp).toLocaleString()}
                </div>
              </div>
            ))}
            {analyticsData.clickHistory.length === 0 && (
              <div className="no-activity">
                <p>No activity recorded yet</p>
                <p>Start using apps to see analytics!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;